import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { gradientFor, initialsFor } from "../../components/Avatar/avatarLook";
import { countryName } from "../../components/CountryCell/countryName";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { visibilityLabel } from "../guilds/visibility";
import ec from "../leaderboards/EntityCell.module.css";
import gm from "../guilds/GuildMembersTable.module.css";

import { formatDate } from "./formatTime";
import type { PlayerGuild, PlayerGuildRole } from "./types";
import card from "./PlayerReferralsCard.module.css";
import styles from "./PlayerGuildCard.module.css";

const ROLE_LABEL: Record<PlayerGuildRole, string> = {
  leader: "Leader",
  officer: "Officer",
  member: "Member",
};

interface Props {
  projectId: string;
  /** The answer — null for a player in no guild, undefined while in flight. */
  data: PlayerGuild | null | undefined;
  loading: boolean;
  failed: boolean;
}

/**
 * The guild the player is in, drawn as the Invites card's «Invited by» line:
 * who they are in it, who leads it, and how full it is.
 *
 * The query lives on the page; like its neighbours the card draws its own
 * loading and failure states, so neither holds back the rest of the page.
 */
export function PlayerGuildCard({ projectId, data, loading, failed }: Props) {
  let body: ReactNode;
  let sub: ReactNode = null;

  if (failed && data === undefined) {
    body = (
      <div className={card.note} role="status">
        Couldn&apos;t load the guild.
      </div>
    );
  } else if (loading || data === undefined) {
    body = <GuildSkeleton />;
  } else if (data === null) {
    body = <div className={card.note}>Not in a guild.</div>;
  } else {
    sub = (
      <span className={styles.cardSub}>
        member since {formatDate(data.joined_at)}
      </span>
    );
    body = <GuildRow projectId={projectId} guild={data} />;
  }

  return (
    <div className={card.card}>
      <div className={card.cardHeader}>
        <h3 className={card.cardTitle}>Guild</h3>
        {sub}
      </div>
      <div className={card.cardBody}>{body}</div>
    </div>
  );
}

function GuildRow({
  projectId,
  guild,
}: {
  projectId: string;
  guild: PlayerGuild;
}) {
  const guildPath = `/projects/${projectId}/guilds/${guild.id}`;
  return (
    <div className={styles.row}>
      <span
        className={`${ec.avatar} ${ec.emblem} ${styles.emblem}`}
        style={{ background: gradientFor(guild.name) }}
        aria-hidden="true"
      >
        {initialsFor(guild.name)}
      </span>
      <div className={card.inviterMain}>
        <div className={`${card.inviterName} ${styles.name}`}>
          <Link to={guildPath}>{guild.name}</Link>
          <span className={`${gm.roleBadge} ${gm[guild.role]}`}>
            {ROLE_LABEL[guild.role]}
          </span>
        </div>
        <div className={`${card.inviterSub} ${styles.sub}`}>
          {guild.role === "leader" ? (
            "Leader of the guild"
          ) : (
            <>
              Leader ·{" "}
              <Link to={`/projects/${projectId}/players/${guild.leader_id}`}>
                {guild.leader_name}
              </Link>
            </>
          )}
        </div>
      </div>
      <div className={styles.stats}>
        <div>
          <div className={styles.statKey}>Members</div>
          <div className={styles.statVal}>
            {guild.member_count} / {guild.max_members}
          </div>
        </div>
        <div>
          <div className={styles.statKey}>Visibility</div>
          <div className={styles.statVal}>
            {visibilityLabel(guild.visibility)}
          </div>
        </div>
        <div>
          <div className={styles.statKey}>Country</div>
          <div className={styles.statVal}>
            {guild.country ? countryName(guild.country) : "—"}
          </div>
        </div>
      </div>
      <Link to={guildPath} className={styles.btn}>
        Open guild
        <ChevronRight size={13} aria-hidden />
      </Link>
    </div>
  );
}

/** The row's shape while the answer is in flight: the emblem and two lines. */
function GuildSkeleton() {
  return (
    <div className={styles.row}>
      <Skeleton width={38} height={38} radius={9} />
      <div className={styles.skelLines}>
        <Skeleton width="30%" height={13} />
        <Skeleton width="20%" height={11} />
      </div>
    </div>
  );
}
